import React, { createContext, useState, useEffect, useContext } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInAnonymously, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'; // Import functions services

const AuthContext = createContext();
const FirebaseContext = createContext();

export const useAuth = () => useContext(AuthContext);
export const useFirebase = () => useContext(FirebaseContext);

export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [auth, setAuth] = useState(null);
    const [db, setDb] = useState(null);
    const [storage, setStorage] = useState(null);
    const [functions, setFunctions] = useState(null); // Add state for functions
    const [isAuthReady, setIsAuthReady] = useState(false);

    useEffect(() => {
        console.log("AuthContext: Initializing Firebase...");
        const firebaseConfig = {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID,
          measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
        };

        if (firebaseConfig.apiKey) {
            try {
                const app = initializeApp(firebaseConfig);
                const firebaseAuth = getAuth(app);
                const firestoreDb = getFirestore(app);
                const firebaseStorage = getStorage(app);
                const firebaseFunctions = getFunctions(app); // Initialize functions
                
                // --- ADDED: Connect to emulators when running locally ---
                if (window.location.hostname === "localhost") {
                  console.log("AuthContext: Localhost detected, connecting to emulators...");
                  connectAuthEmulator(firebaseAuth, "http://127.0.0.1:9099", { disableWarnings: true });
                  connectFirestoreEmulator(firestoreDb, "127.0.0.1", 8080);
                  connectFunctionsEmulator(firebaseFunctions, "localhost", 5001);
                }
                // --- END EMULATOR BLOCK ---
                
                setAuth(firebaseAuth);
                setDb(firestoreDb);
                setStorage(firebaseStorage);
                setFunctions(firebaseFunctions); // Set functions state
                console.log("AuthContext: Firebase Initialized successfully.");

                const unsubscribe = onAuthStateChanged(firebaseAuth, (currentUser) => {
                    console.log("AuthContext: onAuthStateChanged fired.");
                    if (currentUser) {
                        console.log(`AuthContext: User found (UID: ${currentUser.uid}). User is anonymous: ${currentUser.isAnonymous}`);
                        setUser(currentUser);
                    } else {
                        console.log("AuthContext: No user found. Attempting anonymous sign-in...");
                        signInAnonymously(firebaseAuth)
                            .then(() => {
                                console.log("AuthContext: Anonymous sign-in successful.");
                            })
                            .catch(error => {
                                console.error("AuthContext: Anonymous sign-in FAILED:", error);
                            });
                    }
                    setIsAuthReady(true);
                });
                return () => unsubscribe();
            } catch (error) {
                console.error('AuthContext: Firebase Initialization Error:', error);
                setIsAuthReady(true);
            }
        } else {
             console.error("AuthContext: Firebase config is missing from .env file.");
             setIsAuthReady(true);
        }
    }, []);

    const authValue = { user, auth, isAuthReady };
    // Add functions to the provided firebase value
    const firebaseValue = { db, storage, functions };

    return (
        <AuthContext.Provider value={authValue}>
            <FirebaseContext.Provider value={firebaseValue}>
                {isAuthReady ? children : <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">Connecting...</div>}
            </FirebaseContext.Provider>
        </AuthContext.Provider>
    );
};